import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics } from '@atlassian/analytics-bridge';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { OPERATIONS } from '../../common/constants';
import {
	type ChangeOrderRoutingRuleParams,
	type ElseSelectOptionType,
	type Entity,
	MatchConditionType,
	type RouteOptionsType,
	type RoutingRuleFormValues,
} from '../../common/types';
import { createRouteAlertOptions, transformTimeToObject } from '../../common/utils';
import { routingRulesResource } from '../../services/resources';
import { useRoutingRuleService } from '../../services/routing-rule';

import { type useRoutingRulesControllerProps } from './types';

export const useRoutingRulesController = ({ schedules }: useRoutingRulesControllerProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data } = useResource(routingRulesResource);

	assert(
		!!data,
		new AssertionError(
			'useRoutingRulesController',
			'We should not render useRoutingRulesController before data is fetched',
		),
	);

	const { formatMessage } = useIntl();
	const [editingData, setEditingData] = useState<Entity>();
	const [isCloning, setIsCloning] = useState(false);
	const [lastRuleValue, setLastRuleValue] = useState<ElseSelectOptionType | null>();
	const [routeToOptions, setRouteToOptions] = useState<RouteOptionsType>([]);
	const [isRoutingRuleModalOpen, setIsRoutingRuleModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deletingId, setDeletingId] = useState('');
	const {
		createRoutingRule,
		updateRoutingRule,
		deleteRoutingRule,
		changeOrderRoutingRule,
		isDeleting,
		isCreating,
		isUpdating,
		isChangingOrder,
		saveView,
		completeTask,
	} = useRoutingRuleService();

	useEffect(() => {
		if (data) {
			const options = createRouteAlertOptions(formatMessage, data, schedules);

			setRouteToOptions(options);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, schedules]);

	const openRoutingRuleModal = () => setIsRoutingRuleModalOpen(true);
	const closeRoutingRuleModal = () => {
		setIsRoutingRuleModalOpen(false);
		setIsCloning(false);
		setEditingData(undefined);
	};

	const openDeleteModal = () => setIsDeleteModalOpen(true);
	const closeDeleteModal = () => {
		setIsDeleteModalOpen(false);
		setIsCloning(false);
		setEditingData(undefined);
	};

	const handleEditClick = (data: Entity) => {
		setEditingData(data);
		openRoutingRuleModal();
		completeTask();
	};

	const handleCloneClick = (data: Entity) => {
		setIsCloning(true);
		setEditingData(data);
		openRoutingRuleModal();
	};

	const handleDeleteClick = (data: Entity) => {
		setEditingData(data);
		openDeleteModal();
	};

	const handleSubmitRule = (values: RoutingRuleFormValues) => {
		let timeRestrictions = values.timeRestrictions;

		if (values.start && values.end) {
			timeRestrictions = [transformTimeToObject(values.start, values.end)];
		}

		const isNoOne = values.entity.value === 'noOne';

		const formattedValues = {
			name: values.name,
			conditionMatchType: MatchConditionType[values.conditions.matchType],
			conditions: values.conditions.conditions.map((condition) => ({
				...condition,
				operation: OPERATIONS.find((operation) => operation.name === condition.operation)!.id,
			})),
			timeRestrictedInterval: {
				appliedTimeZone: editingData?.timeRestrictedInterval.appliedTimeZone || '',
				enabled: values.timeRestrictedIntervalEnabled,
				timeRestrictions: timeRestrictions,
			},
			entityId: isNoOne ? '' : values.entity.value,
			entityType: isNoOne ? '' : values.entity.type,
			timeZoneId: values.timeZoneId?.value || null,
			ruleTarget: isNoOne ? 0 : 1,
		};

		if (editingData && !isCloning) {
			const updatedData = {
				...formattedValues,
				id: editingData.id,
				isDefault: false,
			};

			updateRoutingRule(updatedData)
				.then(() => closeRoutingRuleModal())
				.then(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'routingRule edited', editingData.id);
				})
				.catch(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'routingRule editFailed', editingData.id);
				});
		} else {
			createRoutingRule(formattedValues)
				.then((res) => {
					closeRoutingRuleModal();

					const event = createAnalyticsEvent({});

					if (isCloning && editingData) {
						fireTrackAnalytics(event, `routingRule cloned`, editingData.id);

						return;
					}

					fireTrackAnalytics(event, `routingRule added`);
				})
				.catch(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'routingRule createFailed');
				});
		}
	};

	const handleChangeLastRule = (entity: Entity, changedValue: ElseSelectOptionType) => {
		setLastRuleValue(changedValue);

		const isNoOne = changedValue.value === 'noOne';

		const formattedValues = {
			...entity,
			entityId: isNoOne ? '' : changedValue.value,
			entityType: isNoOne ? '' : changedValue.type,
			ruleTarget: isNoOne ? 0 : 1,
		};

		updateRoutingRule({
			...formattedValues,
			isDefault: true,
		}).catch(() => {
			setLastRuleValue(lastRuleValue);
		});
	};

	const handleDeleteRule = () => {
		if (editingData) {
			setDeletingId(editingData.id);
			deleteRoutingRule(editingData.id, editingData.name)
				.then(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'routingRule deleted', editingData.id);
				})
				.catch(() => {
					const event = createAnalyticsEvent({});

					fireTrackAnalytics(event, 'routingRule deleteFailed', editingData.id);
				})
				.finally(() => setDeletingId(''));
			closeDeleteModal();
		}
	};

	const handleChangeOrder = (payload: ChangeOrderRoutingRuleParams, updatedValue: Entity[]) => {
		changeOrderRoutingRule(payload, updatedValue)
			.then(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'routingRuleOrder changed', payload.id, {
					order: payload.targetIndex,
				});
			})
			.catch(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'routingRuleOrder failed', payload.id, {
					order: payload.targetIndex,
				});
			});
	};

	const handleSaveView = () => {
		saveView()
			.then(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'routingRule collapsed');
			})
			.catch(() => {
				const event = createAnalyticsEvent({});

				fireTrackAnalytics(event, 'routingRule collapseFailed');
			});
	};

	return {
		isCreating,
		isUpdating,
		isCloning,
		isDeleting,
		isDeleteModalOpen,
		isChangingOrder,
		isRoutingRuleModalOpen,
		routeToOptions,
		editingData,
		lastRuleValue,
		deletingId,

		handleDeleteClick,
		handleEditClick,
		handleCloneClick,
		openRoutingRuleModal,
		closeRoutingRuleModal,
		openDeleteModal,
		closeDeleteModal,

		setLastRuleValue,
		handleSubmitRule,
		handleChangeLastRule,
		handleDeleteRule,
		handleChangeOrder,
		toggleSection: handleSaveView,
	};
};
